import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataContextService } from '../data-context.service';
import { PluginContextService } from '../plugin-context.service';
import * as Cesium from 'cesium';
import { GlobeService } from '../globe.service';

export interface Incident {
  id: string;
  name: string;
  description: string;
  focalPointGeo?: any;
  highGeometries: [{ coordinates: number[][][] }];
  updatedAt: Date;
  createdAt: Date;
  status: string;
  geometry?: {
    type: string;
    coordinates: number[][];
    focalPoint?: {
      longitude: number;
      latitude: number;
    } | null;
  };
}

@Injectable({
  providedIn: 'root'
})
export class IncidentCreatorService {
  private currentIncidentSubject = new BehaviorSubject<Incident | null>(null);
  public currentIncident$ = this.currentIncidentSubject.asObservable();

  private drawingStateSubject = new BehaviorSubject<'drawshape' | 'focalpoint' | 'addpoint'>('drawshape');
  public drawingState$ = this.drawingStateSubject.asObservable();

  private viewer: any;
  private positions: Cesium.Cartesian3[] = [];
  private currentPolygon: any;
  private focalPoint: any;
  private handler: Cesium.ScreenSpaceEventHandler | null = null;

  constructor(
    private pluginContext: PluginContextService,
    private dataContext: DataContextService,
    private globeService: GlobeService
  ) {
    this.viewer = this.globeService.getViewer();
  }

  /**
   * Starts the incident creation process
   */
  startIncidentCreation(): void {
    this.drawingStateSubject.next('drawshape');
    this.positions = [];
    this.focalPoint = null;
    this.setupDrawingHandler();
  }

  /**
   * Sets up the drawing handler for the globe
   */
  private setupDrawingHandler(): void {
    if (!this.viewer) return;

    // Remove existing handler if any
    if (this.handler) {
      this.handler.destroy();
    }

    this.handler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);

    // Handle left click for adding points
    this.handler.setInputAction((event: any) => {
      const position = this.viewer.camera.pickEllipsoid(event.position);
      if (position) {
        this.addPoint(position);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Handle double click to finish drawing
    this.handler.setInputAction((event: any) => {
      if (this.positions.length >= 3) {
        this.finishDrawing();
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  }

  /**
   * Adds a point to the drawing
   */
  private addPoint(position: Cesium.Cartesian3): void {
    this.positions.push(position);
    
    // Update or create polygon
    if (this.currentPolygon) {
      this.viewer.entities.remove(this.currentPolygon);
    }
    
    this.currentPolygon = this.viewer.entities.add({
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(this.positions),
        material: Cesium.Color.RED.withAlpha(0.5),
        outline: true,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
         height: 0 
      }
    });

    // Add point marker
    this.viewer.entities.add({
      position: position,
      point: {
        pixelSize: 8,
        color: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 1
      }
    });
  }

  /**
   * Finishes the drawing process
   */
  finishDrawing(): void {
    if (this.positions.length >= 3) {
      this.drawingStateSubject.next('focalpoint');
      this.cleanupHandler();
    }
  }

  /**
   * Cancels the drawing process
   */
  cancelDrawing(): void {
    this.cleanupDrawing();
    this.drawingStateSubject.next('drawshape');
  }

  /**
   * Sets the focal point for the incident
   */
  setFocalPoint(): void {
    this.drawingStateSubject.next('addpoint');
    this.setupFocalPointHandler();
  }

  /**
   * Sets up the handler for focal point placement
   */
  private setupFocalPointHandler(): void {
    if (!this.viewer) return;

    if (this.handler) {
      this.handler.destroy();
    }

    this.handler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);
    this.handler.setInputAction((event: any) => {
      const position = this.viewer.camera.pickEllipsoid(event.position);
      if (position) {
        this.addFocalPoint(position);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  /**
   * Adds a focal point to the incident
   */
  private addFocalPoint(position: Cesium.Cartesian3): void {
    if (this.focalPoint) {
      this.viewer.entities.remove(this.focalPoint);
    }
    
    this.focalPoint = this.viewer.entities.add({
      position: position,
      point: {
        pixelSize: 12,
        color: Cesium.Color.YELLOW,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2
      }
    });
    
    this.drawingStateSubject.next('focalpoint');
    this.cleanupHandler();
  }

  /**
   * Continues to the next step after setting focal point
   */
  continue(): void {
    if (this.focalPoint && this.positions.length >= 3) {
      const incident: Partial<Incident> = {
        name: 'New Incident',
        description: 'Created from map',
        geometry: {
          type: 'Polygon',
          coordinates: this.positions.map(pos => {
            const cartographic = Cesium.Cartographic.fromCartesian(pos);
            return [
              Cesium.Math.toDegrees(cartographic.longitude),
              Cesium.Math.toDegrees(cartographic.latitude)
            ];
          }),
          focalPoint: this.focalPoint ? {
            longitude: Cesium.Math.toDegrees(Cesium.Cartographic.fromCartesian(this.focalPoint.position.getValue()).longitude),
            latitude: Cesium.Math.toDegrees(Cesium.Cartographic.fromCartesian(this.focalPoint.position.getValue()).latitude)
          } : null
        }
      };
      this.createIncident(incident);
      this.cleanup();
    }
  }

  /**
   * Cancels the focal point setting
   */
  cancelFocalPoint(): void {
    if (this.focalPoint) {
      this.viewer.entities.remove(this.focalPoint);
      this.focalPoint = null;
    }
    this.drawingStateSubject.next('drawshape');
    this.cleanupHandler();
  }

  /**
   * Creates a new incident
   */
  createIncident(incident: Partial<Incident>): void {
    const newIncident: Incident = {
      id: this.generateId(),
      name: incident.name || '',
      description: incident.description || '',
      geometry: incident.geometry,
      status: 'active',
      createdAt: new Date(),
      updatedAt: new Date(),
      highGeometries: [{
        coordinates: []
      }]
    };

    this.currentIncidentSubject.next(newIncident);
  }

  /**
   * Generates a unique ID for incidents
   */
  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  /**
   * Cleans up the drawing handler
   */
  private cleanupHandler(): void {
    if (this.handler) {
      this.handler.destroy();
      this.handler = null;
    }
  }

  /**
   * Cleans up all drawing resources
   */
  private cleanupDrawing(): void {
    this.cleanupHandler();
    
    if (this.currentPolygon) {
      this.viewer.entities.remove(this.currentPolygon);
      this.currentPolygon = undefined;
    }
    
    if (this.focalPoint) {
      this.viewer.entities.remove(this.focalPoint);
      this.focalPoint = null;
    }
    
    // Remove all point markers
    this.viewer.entities.values.forEach((entity: any) => {
      if (entity.point) {
        this.viewer.entities.remove(entity);
      }
    });
    
    this.positions = [];
  }

  /**
   * Cleans up all resources
   */
  private cleanup(): void {
    this.cleanupDrawing();
    this.drawingStateSubject.next('drawshape');
  }
}